import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  DataTable,
  EmptyState,
  ErrorNotice,
  Heading,
  PageHeading,
  Panel,
  Pill,
  ProgressBar,
  SearchInput,
  SkeletonTable,
  StatTile,
  TrendChart,
  type DataTableCard,
  type DataTableSort,
  type TrendPoint,
  type TrendSeries,
} from '@bcecloud/portal-ui';
import { errorMessage } from '@/lib/api';
import { LIVE_REFRESH_MS } from '@/lib/report-window';
import { filterRows, sortRows } from '@/lib/facet-table';
import { useTableSort } from '@/lib/use-table-sort';
import { useUrlSearch } from '@/lib/use-list-query-state';
import { InfoTip } from '@/components/metric-info';
import {
  AB_TEST_STATUS,
  PAGE_TEST_VERSION,
  STATUS_PILL,
  VERDICT_LABELS,
  VERSION_LABELS,
  readPageTests,
  type AbTestReport,
  type AbTestVersionMetrics,
  type PageTestVersion,
} from '@/lib/page-tests';

/**
 * Analytics → Page tests: each A/B page test on the website, read on
 * qualified leads per paid session (GET /analytics/ab-tests). Every number
 * before a test's planned read date is labeled as not a result; the page
 * only names a winner once the API says the test is ready to read.
 */

const SITE_ORIGIN = 'https://bestcorporateevents.com';
const HELP_PATH = '/help/analytics/page-tests';

const LEAD_SORT_FIELDS = {
  submittedAt: 'submittedAt',
  company: 'company',
  version: 'version',
  status: 'status',
  paid: 'paidLabel',
} as const;
const LEAD_SORT_KEYS = Object.values(LEAD_SORT_FIELDS);
const NEWEST_FIRST: DataTableSort = { key: LEAD_SORT_FIELDS.submittedAt, direction: 'desc' };
const LEAD_SEARCH_FIELDS = ['company', 'status', 'versionLabel', 'paidLabel'] as const;

const PAID_LABEL = { yes: 'Paid', no: 'Not paid' } as const;

const count = (value: number): string => value.toLocaleString('en-US');
const rate = (value: number | null): string => (value === null ? 'None yet' : `${(value * 100).toFixed(2)}%`);
const chance = (value: number | null): string => (value === null ? 'None yet' : `${Math.round(value * 100)}%`);
const signedPercent = (value: number | null): string =>
  value === null ? 'no baseline yet' : `${value >= 0 ? '+' : ''}${Math.round(value * 100)}%`;
const dayLabel = (ymd: string): string =>
  new Date(`${ymd}T12:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
const shortDay = (ymd: string): string =>
  new Date(`${ymd}T12:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
const dateLabel = (iso: string): string =>
  new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

/** True before GA4 reports a test's first day and before any lead carries its version. */
function hasNoData(report: AbTestReport): boolean {
  return report.sessionsThrough === null && report.leads.length === 0;
}

/**
 * @returns The Page tests page.
 */
export function PageTestsAnalyticsPage(): React.ReactElement {
  const tests = useQuery({
    queryKey: ['page-tests'],
    queryFn: ({ signal }) => readPageTests(signal),
    refetchInterval: LIVE_REFRESH_MS,
  });
  return (
    <section className="animate-portal-rise">
      <PageHeading
        eyebrow="ANALYTICS"
        title="Page tests"
        subtitle="A/B tests of website pages, judged on qualified leads per paid session. Numbers before a test's planned read date are not a result: they swing a lot early and are shown only to catch problems."
        action={
          <Link to={HELP_PATH} className="text-portal-small font-bold text-brand-link hover:underline">
            How to read this
          </Link>
        }
      />
      {tests.isError ? (
        <ErrorNotice title="Page tests unavailable" message={errorMessage(tests.error)} />
      ) : tests.isPending ? (
        <SkeletonTable label="Loading page tests…" />
      ) : tests.data.tests.length === 0 ? (
        <Panel>
          <EmptyState title="No page tests" message="Tests show here once they are added to the test registry and go live." />
        </Panel>
      ) : (
        <div className="space-y-12">
          {tests.data.tests.map((report) => (
            <TestSection key={report.test.key} report={report} />
          ))}
        </div>
      )}
    </section>
  );
}

function TestSection({ report }: { readonly report: AbTestReport }): React.ReactElement {
  const { test } = report;
  const pill = STATUS_PILL[report.status];
  const ready = report.status === AB_TEST_STATUS.readyToRead;
  return (
    <section aria-labelledby={`test-${test.short}`}>
      <div className="mb-4 border-b border-portal-line pb-4">
        <div className="flex flex-wrap items-center gap-3">
          <Heading variant="section" as="h2">
            <span id={`test-${test.short}`}>
              {test.short.toUpperCase()}: {test.name}
            </span>
          </Heading>
          <Pill tone={pill.tone}>{pill.label}</Pill>
        </div>
        <p className="mt-1 text-portal-small text-portal-muted">
          <a href={`${SITE_ORIGIN}${test.path}`} target="_blank" rel="noreferrer" className="text-brand-link hover:underline">
            {test.path}
          </a>
          {' · '}Started {dayLabel(test.startedAt)}
          {' · '}Planned read {dayLabel(test.plannedReadAt)}
          {' · '}Day {report.progress.daysRun + 1}
        </p>
        <p className="mt-1 text-portal-small">{test.hypothesis}</p>
      </div>

      {report.status === AB_TEST_STATUS.srmWarning ? <ErrorNotice inline tone="warning" message={report.statusReason} /> : null}
      {report.status === AB_TEST_STATUS.earlyHarm ? <ErrorNotice inline message={report.statusReason} /> : null}

      {hasNoData(report) ? (
        <Panel>
          <EmptyState
            title="No results yet"
            message="Results start the day after a test goes live. GA4 reports each day's sessions about a day later, and leads appear here as they reach Salesforce."
          />
        </Panel>
      ) : (
        <>
          <p className="mb-3 text-portal-small">
            {ready && report.verdict !== null ? (
              <span className="font-bold">{VERDICT_LABELS[report.verdict]}</span>
            ) : (
              <>
                <span className="font-bold">Not a result yet.</span> These numbers move a lot until the planned read on{' '}
                {dayLabel(test.plannedReadAt)}. Use them only to spot a broken split or clear harm.
              </>
            )}
          </p>
          <VersionTiles report={report} />
          <ProgressBar
            className="mt-6"
            label={`${test.short.toUpperCase()} progress to planned paid sessions`}
            value={report.progress.paidSessions}
            max={report.progress.plannedPaidSessions}
            caption={`${count(report.progress.paidSessions)} of ${count(report.progress.plannedPaidSessions)} paid sessions planned (enough to spot a doubling of the ${(test.baselinePaidCvr * 100).toFixed(2)}% baseline)`}
          />
          <QualifiedChart report={report} />
          <LeadsTable report={report} />
          <p className="mt-2 text-portal-micro text-portal-muted">
            Sessions through {report.sessionsThrough === null ? 'none yet' : dayLabel(report.sessionsThrough)}. Rates count leads through the same day.
            {report.unmatchedSubmissions > 0
              ? ` ${count(report.unmatchedSubmissions)} submission${report.unmatchedSubmissions === 1 ? ' has' : 's have'} a version but no Salesforce lead yet and ${report.unmatchedSubmissions === 1 ? 'is' : 'are'} not counted.`
              : ''}
          </p>
        </>
      )}
    </section>
  );
}

/** One tile per metric: A then B, with B against A underneath. */
function VersionTiles({ report }: { readonly report: AbTestReport }): React.ReactElement {
  const { a, b } = report.versions;
  const { comparison } = report;
  const pair = (pick: (metrics: AbTestVersionMetrics) => string): string => `${pick(a)} vs ${pick(b)}`;
  return (
    <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-5">
      <StatTile label="Paid sessions, A vs B" value={pair((m) => count(m.paidSessions))} hint={`${count(a.sessions + b.sessions)} sessions in all`} />
      <StatTile label="Paid leads, A vs B" value={pair((m) => count(m.paidLeads))} hint={`${count(a.qualifiedPaidLeads)} vs ${count(b.qualifiedPaidLeads)} qualified`} />
      <StatTile label="Paid conversion rate" value={pair((m) => rate(m.paidCvr))} hint={`B vs A: ${signedPercent(comparison.paidCvrLift)}`} />
      <StatTile
        label="Qualified per paid session"
        labelAccessory={<InfoTip label="qualified per paid session" text="Paid leads not closed as unqualified, per Google Ads session. The number each test is judged on." />}
        value={pair((m) => rate(m.qualifiedPerPaidSession))}
        hint={`B vs A: ${signedPercent(comparison.qualifiedPerPaidSessionLift)}`}
      />
      <StatTile
        label="Chance B is better"
        labelAccessory={
          <InfoTip
            label="chance B is better"
            text="How likely B's qualified leads per paid session beat A's, given the data so far. B ships only at 90% or more, read on or after the planned date."
          />
        }
        value={chance(comparison.probabilityBBetter)}
        hint="Read at the planned date, not before"
      />
    </div>
  );
}

const SERIES_LABEL: Readonly<Record<PageTestVersion, string>> = { a: 'Version A', b: 'Version B' };

/** Cumulative qualified leads per paid session by day, one line per version. */
function QualifiedChart({ report }: { readonly report: AbTestReport }): React.ReactElement | null {
  const chart = useMemo(() => {
    const percent = (value: number | null): number => (value === null ? 0 : Math.round(value * 10000) / 100);
    const points: TrendPoint[] = report.series.map((day) => {
      const sessions = day.versions.a.paidSessions + day.versions.b.paidSessions;
      return {
        key: day.date,
        label: shortDay(day.date),
        value: percent(Math.max(day.versions.a.cumulativeQualifiedPerPaidSession ?? 0, day.versions.b.cumulativeQualifiedPerPaidSession ?? 0)),
        details: [`${count(sessions)} paid sessions that day`],
      };
    });
    const series: TrendSeries[] = [PAGE_TEST_VERSION.a, PAGE_TEST_VERSION.b].map((version) => ({
      key: version,
      label: SERIES_LABEL[version],
      values: report.series.map((day) => percent(day.versions[version].cumulativeQualifiedPerPaidSession)),
    }));
    return { points, series };
  }, [report.series]);
  if (report.series.length === 0) return null;
  return (
    <div className="mt-6">
      <Heading variant="card" as="h3" className="mb-1">
        Qualified leads per paid session, running total
      </Heading>
      <p className="mb-2 text-portal-micro text-portal-muted">Each day adds to the totals since the test started. Early days jump around.</p>
      <Panel>
        <TrendChart
          title={`${report.test.short.toUpperCase()} qualified leads per paid session`}
          unit="qualified per paid session"
          variant="line"
          points={chart.points}
          series={chart.series}
          height={220}
          formatValue={(value) => `${value.toFixed(2)}%`}
        />
      </Panel>
    </div>
  );
}

function LeadsTable({ report }: { readonly report: AbTestReport }): React.ReactElement {
  const prefix = report.test.short;
  const { sort, onSort } = useTableSort(LEAD_SORT_KEYS, NEWEST_FIRST, { prefix });
  const search = useUrlSearch(`${prefix}_q`);
  const flat = useMemo(
    () =>
      report.leads.map((lead) => ({
        ...lead,
        versionLabel: VERSION_LABELS[lead.version],
        paidLabel: lead.paid ? PAID_LABEL.yes : PAID_LABEL.no,
      })),
    [report.leads],
  );
  const rows = useMemo(() => sortRows(filterRows(flat, search.search, LEAD_SEARCH_FIELDS), sort), [flat, search.search, sort]);
  const cards: DataTableCard[] = rows.map((row) => ({
    key: row.salesforceId,
    title: row.company || 'No company',
    subtitle: `${dateLabel(row.submittedAt)} · ${row.versionLabel}`,
    fields: [
      { label: 'Salesforce status', value: row.status || 'None' },
      { label: 'Paid', value: row.paidLabel },
    ],
  }));
  return (
    <div className="mt-6">
      <Heading variant="card" as="h3" className="mb-1">
        Leads in this test
      </Heading>
      <p className="mb-3 text-portal-micro text-portal-muted">Each Salesforce lead from a visitor who saw this page, with the version they saw.</p>
      {report.leads.length === 0 ? (
        <Panel>
          <EmptyState title="No leads yet" message="Leads show here once a visitor who saw this page fills in the form and the lead reaches Salesforce." />
        </Panel>
      ) : (
        <>
          <SearchInput
            label={`Search ${prefix.toUpperCase()} leads`}
            placeholder="Company, version or status"
            value={search.search}
            onChange={(event) => search.setSearch(event.target.value)}
            className="mb-3 w-full max-w-sm"
          />
          {rows.length === 0 ? (
            <Panel>
              <EmptyState title="Nothing matches" message="Try a different search." />
            </Panel>
          ) : (
            <DataTable
              headers={[
                { label: 'Date', sortKey: LEAD_SORT_FIELDS.submittedAt },
                { label: 'Company', sortKey: LEAD_SORT_FIELDS.company },
                { label: 'Version', sortKey: LEAD_SORT_FIELDS.version },
                { label: 'Salesforce status', sortKey: LEAD_SORT_FIELDS.status },
                { label: 'Paid', sortKey: LEAD_SORT_FIELDS.paid },
              ]}
              sort={sort}
              onSort={onSort}
              cards={cards}
            >
              {rows.map((row) => (
                <tr key={row.salesforceId} className="border-t border-portal-line align-middle">
                  <td className="p-3 text-portal-small whitespace-nowrap text-portal-muted">{dateLabel(row.submittedAt)}</td>
                  <td className="p-3 text-portal-small font-bold">{row.company || <span className="text-portal-muted">No company</span>}</td>
                  <td className="p-3 text-portal-small whitespace-nowrap">{row.versionLabel}</td>
                  <td className="p-3 text-portal-small">{row.status || <span className="text-portal-muted">None</span>}</td>
                  <td className="p-3 text-portal-small text-portal-muted">{row.paidLabel}</td>
                </tr>
              ))}
            </DataTable>
          )}
        </>
      )}
    </div>
  );
}
